import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, Check, FileText, Loader2, Circle } from "lucide-react";
import { WizardProgress, WizardFooter, Button, Pill, Card, CardBody, NarrativeCard } from "@/design/components";
import { ImpactLedgerLogo } from "@/shell/ImpactLedgerLogo";
import { useActiveWorkspace } from "@/domain/hooks";

const STEPS = [
  { key: "upload", label: "Upload" },
  { key: "review", label: "Review" },
  { key: "bulk", label: "Bulk edit" },
  { key: "preview", label: "Preview" },
  { key: "commit", label: "Create" },
];

export function UploadWizard() {
  const ws = useActiveWorkspace();
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [uploaded, setUploaded] = useState(false);

  return (
    <div className="flex min-h-screen flex-col bg-bg">
      <header className="flex h-16 items-center justify-between gap-2 border-b border-line bg-card px-4 sm:px-6">
        <ImpactLedgerLogo />
        <div className="flex items-center gap-4">
          <span className="hidden text-sm font-medium text-ink sm:inline">AI procurement plan upload</span>
          <button onClick={() => navigate(`/workspace/${workspaceId}`)} className="text-sm text-mute hover:text-ink">Exit</button>
        </div>
      </header>
      <div className="border-b border-line bg-card px-6 py-3">
        <WizardProgress steps={STEPS} currentIndex={step} onStepClick={(i) => i < step && setStep(i)} />
      </div>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl px-6 py-8">
          {step === 0 && <StepUpload uploaded={uploaded} onUpload={() => setUploaded(true)} />}
          {step === 1 && <StepReview />}
          {step === 2 && <StepBulk />}
          {step === 3 && <StepPreview ws={ws} />}
          {step === 4 && <StepCommit onDone={() => navigate(`/workspace/${workspaceId}`)} />}
        </div>
      </main>

      {step < 4 && (
        <WizardFooter
          showBack={step > 0}
          onBack={() => setStep((s) => s - 1)}
          onContinue={() => setStep((s) => s + 1)}
          continueDisabled={step === 0 && !uploaded}
          continueLabel={step === 3 ? "Create workspace" : "Continue"}
          secondary={<Button variant="ghost" onClick={() => toast.message("Draft saved")}>Save draft</Button>}
        />
      )}
    </div>
  );
}

function StepUpload({ uploaded, onUpload }: { uploaded: boolean; onUpload: () => void }) {
  const [processing, setProcessing] = useState(false);
  const [extracted, setExtracted] = useState(0);

  function handleUpload() {
    if (uploaded || processing) return;
    setProcessing(true);
    setExtracted(0);
    let count = 0;
    const iv = setInterval(() => {
      count++;
      setExtracted(count);
      if (count >= 8) {
        clearInterval(iv);
        setTimeout(() => {
          onUpload();
          toast.success("Document processed. 8 projects, 22 sub-projects extracted.");
          setProcessing(false);
        }, 400);
      }
    }, 220);
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-ink">Upload procurement plan</h1>
      <p className="mt-1 text-sm text-mute">PDF, DOCX or XLSX. Up to 50MB per file.</p>
      <button
        type="button"
        onClick={handleUpload}
        disabled={processing || uploaded}
        className={`mt-5 flex min-h-56 w-full flex-col items-center justify-center rounded-xl border-2 border-dashed transition-colors disabled:cursor-default ${
          uploaded ? "border-ok bg-okSoft" : processing ? "border-brand bg-brandSoft" : "border-line hover:border-brand"
        }`}
      >
        {uploaded ? (
          <>
            <Check className="h-8 w-8 text-ok" />
            <p className="mt-2 text-sm font-medium text-ink">Uganda-Fire-Stations-Procurement-Plan.pdf</p>
            <p className="text-xs text-faint">Processed · 8 projects, 22 sub-projects detected</p>
          </>
        ) : processing ? (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-brand" />
            <p className="mt-2 text-sm font-medium text-ink">Extracting procurement data…</p>
            <p className="tabular-nums text-xs text-brand">
              <span className="font-semibold">{extracted}</span> of 8 projects detected
            </p>
          </>
        ) : (
          <>
            <UploadCloud className="h-8 w-8 text-faint" />
            <p className="mt-2 text-sm font-medium text-ink">Drag and drop, or click to upload</p>
            <p className="text-xs text-faint">PDF, DOCX, XLSX · max 50MB</p>
          </>
        )}
      </button>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <LabeledInput label="Programme name" value="Uganda Fire Stations 2026–2029" />
        <LabeledInput label="Funder" value="Danish Impact Fund" />
        <LabeledInput label="Recipient / lead agency" value="Government of Uganda" />
        <LabeledInput label="Total budget" value="48B UGX" />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Pill variant="brand">Uganda Sovereignty Bill 2026</Pill>
        <Pill variant="brand">DPPA 2019</Pill>
      </div>
    </div>
  );
}

function StepReview() {
  const fields = [
    { label: "Programme name", confidence: "high", value: "Uganda Fire Stations 2026–2029" },
    { label: "Total budget", confidence: "high", value: "48B UGX" },
    { label: "Start date", confidence: "high", value: "15 January 2026" },
    { label: "Procurement line reference", confidence: "medium", value: "PL-2026-FS-004" },
    { label: "Recipient agency", confidence: "medium", value: "Ministry of Local Government" },
    { label: "Approval threshold", confidence: "low", value: "400M UGX" },
    { label: "Contingency buffer", confidence: "low", value: "25%" },
  ];
  const lowFields = fields.filter((f) => f.confidence === "low");
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-12">
      <div className="col-span-7">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-faint">Document preview</h2>
        <div className="rounded-lg border border-line bg-card p-6">
          <div className="mb-4 space-y-1.5">
            <div className="h-4 w-2/3 rounded bg-subtle" />
            <div className="h-3 w-1/2 rounded bg-subtle" />
          </div>
          <div className="space-y-1.5">
            {[90, 85, 95, 70, 88, 62].map((w, i) => (
              <div key={i} className="h-2.5 rounded bg-subtle" style={{ width: `${w}%` }} />
            ))}
          </div>
          <div className="mt-5 rounded border border-brand/40 bg-brandSoft px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-brand/70">Extracted field</p>
            <p className="mt-0.5 text-sm font-medium text-ink">48B UGX</p>
            <p className="mt-0.5 text-[11px] text-brand">Total budget · 96% confidence</p>
          </div>
          <div className="mt-3 space-y-1.5">
            {[78, 55, 91].map((w, i) => (
              <div key={i} className="h-2.5 rounded bg-subtle" style={{ width: `${w}%` }} />
            ))}
          </div>
          <div className="mt-5 rounded border border-warn/40 bg-warnSoft px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-warn/70">Low confidence</p>
            <p className="mt-0.5 text-sm font-medium text-ink">400M UGX</p>
            <p className="mt-0.5 text-[11px] text-warn">Approval threshold · 61% confidence — verify before committing</p>
          </div>
        </div>
      </div>
      <div className="col-span-5 space-y-3">
        {lowFields.length > 0 && (
          <div className="rounded-md border border-warn/30 bg-warnSoft px-3 py-2.5">
            <p className="text-sm font-medium text-warn">{lowFields.length} field{lowFields.length > 1 ? "s" : ""} need your review</p>
            <p className="mt-0.5 text-xs text-warn/80">AI confidence is below threshold. Check these before continuing.</p>
          </div>
        )}
        {fields.map((f) => (
          <div key={f.label} className={`rounded-md border bg-card px-3 py-2.5 ${f.confidence === "low" ? "border-warn/40" : "border-line"}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-faint">{f.label}</p>
              <span className={`text-[10px] font-medium ${f.confidence === "high" ? "text-ok" : f.confidence === "medium" ? "text-warn" : "text-bad"}`}>
                {f.confidence === "high" ? "High" : f.confidence === "medium" ? "Medium" : "Low"}
              </span>
            </div>
            <p className="mt-0.5 truncate text-sm font-medium text-ink">{f.value}</p>
            {f.confidence === "low" && (
              <p className="mt-1 text-[11px] text-warn">Verify this value — extracted with low confidence</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StepBulk() {
  const [selected, setSelected] = useState<string[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const projects = ["Fire Station #04 · Entebbe", "Fire Station #07 · Nansana", "Fire Station #12 · Wakiso", "Fire Station #18 · Kampala Central"];
  const applyOps = ["Set district tag", "Assign contractor", "Apply tag", "Round budgets to 5M"];
  const restructureOps = ["Group under parent", "Split into sub-projects"];

  function handleDelete() {
    if (!deleteConfirm) { setDeleteConfirm(true); return; }
    toast.message(`${selected.length} project${selected.length > 1 ? "s" : ""} deleted`);
    setSelected([]);
    setDeleteConfirm(false);
  }

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-12">
      <div className="col-span-5">
        <div className="mb-2 flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setSelected(projects)}>Select all</Button>
          <Button size="sm" variant="ghost" onClick={() => { setSelected([]); setDeleteConfirm(false); }}>Select none</Button>
        </div>
        <div className="space-y-1.5">
          {projects.map((p) => (
            <label key={p} className="flex items-center gap-2 rounded-md border border-line bg-card px-3 py-2 text-sm">
              <input type="checkbox" checked={selected.includes(p)} onChange={(e) => { setSelected((xs) => e.target.checked ? [...xs, p] : xs.filter((x) => x !== p)); setDeleteConfirm(false); }} className="accent-[var(--accent-base)]" />
              <span className="text-ink">{p}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="col-span-7 space-y-4">
        <div>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Apply {selected.length > 0 && <span className="tabular-nums">· {selected.length} selected</span>}</h2>
          <div className="grid grid-cols-2 gap-2">
            {applyOps.map((o) => (
              <button key={o} disabled={selected.length === 0} onClick={() => toast.message(`${o} applied to ${selected.length} project${selected.length > 1 ? "s" : ""}`)} className="rounded-md border border-line bg-card px-3 py-2.5 text-left text-sm text-ink transition-colors hover:bg-subtle disabled:opacity-40">
                {o}
              </button>
            ))}
          </div>
        </div>
        <div>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Restructure</h2>
          <div className="grid grid-cols-2 gap-2">
            {restructureOps.map((o) => (
              <button key={o} disabled={selected.length === 0} onClick={() => toast.message(`${o} applied to ${selected.length} project${selected.length > 1 ? "s" : ""}`)} className="rounded-md border border-line bg-card px-3 py-2.5 text-left text-sm text-ink transition-colors hover:bg-subtle disabled:opacity-40">
                {o}
              </button>
            ))}
          </div>
        </div>
        <div className="border-t border-line pt-4">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Destructive</h2>
          {deleteConfirm ? (
            <div className="rounded-md border border-bad/30 bg-badSoft px-3 py-3">
              <p className="mb-2 text-sm font-medium text-bad">Delete {selected.length} project{selected.length > 1 ? "s" : ""}? This cannot be undone.</p>
              <div className="flex gap-2">
                <button onClick={handleDelete} className="rounded-md bg-bad px-3 py-1.5 text-xs font-medium text-white hover:bg-bad/90">Confirm delete</button>
                <button onClick={() => setDeleteConfirm(false)} className="rounded-md border border-line px-3 py-1.5 text-xs text-mute hover:text-ink">Cancel</button>
              </div>
            </div>
          ) : (
            <button disabled={selected.length === 0} onClick={handleDelete} className="w-full rounded-md border border-bad/30 bg-card px-3 py-2.5 text-left text-sm text-bad transition-colors hover:bg-badSoft disabled:opacity-40 disabled:text-faint disabled:border-line">
              Delete selected
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepPreview({ ws }: { ws: ReturnType<typeof useActiveWorkspace> }) {
  return (
    <div className="space-y-6">
      <NarrativeCard
        variant="briefing"
        title="Ready to create"
        body="You are about to create 1 Programme, 8 Projects, 22 Sub-projects, 47 Milestones, 12 Contractor shells, and 47 approval matrix entries. The Uganda Sovereignty Bill 2026 + DPPA 2019 compliance pack will be applied at the Programme level and inherited by every Project."
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card><CardBody><p className="text-xs font-semibold uppercase tracking-wide text-faint">Sample project page</p><div className="mt-2 space-y-1.5">{[80, 60, 90, 50].map((w, i) => <div key={i} className="h-2.5 rounded bg-subtle" style={{ width: `${w}%` }} />)}</div></CardBody></Card>
        <Card><CardBody><p className="text-xs font-semibold uppercase tracking-wide text-faint">Branding preview</p><div className="mt-2"><ImpactLedgerLogo /></div><p className="mt-2 text-xs text-faint">Funder: {ws.funder.name}</p></CardBody></Card>
      </div>
      <div>
        <h3 className="mb-2 text-sm font-semibold text-ink">Tier 2 contractor invitations</h3>
        <p className="mb-3 text-xs text-mute">Magic-link onboarding emails will be dispatched on commit. Each contractor will see only their assigned projects and sub-projects.</p>
        <div className="space-y-2">
          {[
            { email: "james@eafe.co.ug", company: "East Africa Fire Engineering Ltd", tier: "Prime · Tier 2", projects: 4 },
            { email: "ops@kbuilders.co.ug", company: "Kampala Builders Co.", tier: "Prime · Tier 2", projects: 3 },
            { email: "info@apexsteel.co.ug", company: "Apex Steel Ltd", tier: "Prime · Tier 2", projects: 2 },
          ].map((c) => (
            <div key={c.email} className="rounded-md border border-line bg-card px-3 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{c.company}</p>
                  <p className="mt-0.5 text-xs text-faint">{c.email}</p>
                </div>
                <Pill variant="info" size="sm">Queued</Pill>
              </div>
              <div className="mt-2 flex gap-3 text-xs text-mute">
                <span>{c.tier}</span>
                <span aria-hidden>·</span>
                <span className="tabular-nums">{c.projects} projects assigned</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StepCommit({ onDone }: { onDone: () => void }) {
  const items = ["Programme created", "Projects and sub-projects scaffolded", "Contractor shells generated", "Invitations queued"];
  const [done, setDone] = useState(0);
  useEffect(() => {
    if (done < items.length) {
      const t = setTimeout(() => setDone((d) => d + 1), 700);
      return () => clearTimeout(t);
    }
  }, [done]);
  const complete = done >= items.length;
  return (
    <div className="mx-auto max-w-md py-10">
      <h1 className="text-center text-2xl font-semibold tracking-tight text-ink">Creating your workspace</h1>
      <ul className="mt-6 space-y-3">
        {items.map((it, i) => (
          <li key={it} className="flex items-center gap-3 rounded-lg border border-line bg-card p-3">
            {i < done ? <Check className="h-5 w-5 text-ok" /> : i === done ? <Loader2 className="h-5 w-5 animate-spin text-brand" /> : <Circle className="h-5 w-5 text-faint" />}
            <span className={`text-sm ${i < done ? "text-ink" : "text-faint"}`}>{it}</span>
          </li>
        ))}
      </ul>
      {complete && (
        <div className="mt-6 text-center">
          <p className="text-base font-semibold text-ok">Workspace ready</p>
          <p className="mt-1 text-sm text-mute">
            1 programme · 8 projects · 22 sub-projects · 47 milestones · 3 contractor invitations sent
          </p>
          <Button className="mt-4 w-full" onClick={onDone}>Go to programme dashboard</Button>
        </div>
      )}
    </div>
  );
}

function LabeledInput({ label, value }: { label: string; value: string }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-faint">{label}</span>
      <input defaultValue={value} className="w-full rounded-md border border-line px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand" />
    </label>
  );
}
